import type { InterviewQuestion } from '../api/interview';

export function taskIdFromQuestion(q: InterviewQuestion): string | null {
  for (const t of q.tags || []) {
    if (typeof t === 'string' && t.startsWith('task:')) {
      return t.slice('task:'.length);
    }
  }
  return null;
}

export function groupQuestionsByTask(
  questions: InterviewQuestion[],
): Map<string, InterviewQuestion[]> {
  const map = new Map<string, InterviewQuestion[]>();
  for (const q of questions) {
    const tid = taskIdFromQuestion(q) || `q_${q.id}`;
    if (!map.has(tid)) map.set(tid, []);
    map.get(tid)!.push(q);
  }
  Array.from(map.values()).forEach((steps) => {
    steps.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  });
  return map;
}

function hashSeeded(key: string, seed: number): number {
  let h = seed >>> 0;
  for (let i = 0; i < key.length; i++) {
    h = ((h * 1103515245) + key.charCodeAt(i) + 12345) >>> 0;
  }
  return h;
}

function areaOfSteps(steps: InterviewQuestion[]): string {
  return (
    (steps[0]?.tags || []).find(
      (t): t is string => typeof t === 'string' && t.startsWith('area:'),
    ) || ''
  )
    .slice('area:'.length)
    .toLowerCase();
}

/** Deterministic pick of one pool task (full interview steps). */
export function pickSampleTaskSteps(
  questions: InterviewQuestion[],
  seed: number,
): InterviewQuestion[] {
  const byTask = groupQuestionsByTask(questions);
  const entries = Array.from(byTask.entries());
  if (entries.length === 0) return [];
  const scored = entries.map(([tid, steps]) => ({ steps, h: hashSeeded(tid, seed) }));
  scored.sort((a, b) => a.h - b.h);
  return scored[0].steps;
}

/**
 * Deterministic pick of a FULL example interview: `total` tasks from the pool,
 * mirroring the backend sampler's distinct-areas preference. Each entry is one
 * case's ordered steps. Falls back to allowing repeated areas if the pool has
 * fewer distinct areas than `total`.
 */
export function pickSampleInterview(
  questions: InterviewQuestion[],
  total: number,
  distinctAreas: boolean,
  seed: number,
): InterviewQuestion[][] {
  const byTask = groupQuestionsByTask(questions);
  const tasks = Array.from(byTask.entries()).map(([tid, steps]) => ({
    steps,
    area: areaOfSteps(steps),
    h: hashSeeded(tid, seed),
  }));
  if (tasks.length === 0) return [];
  tasks.sort((a, b) => a.h - b.h);
  const want = Math.max(1, Math.min(total || 1, tasks.length));
  const picked: typeof tasks = [];
  const usedAreas = new Set<string>();
  if (distinctAreas) {
    for (const tk of tasks) {
      if (picked.length >= want) break;
      if (tk.area && usedAreas.has(tk.area)) continue;
      picked.push(tk);
      if (tk.area) usedAreas.add(tk.area);
    }
  }
  for (const tk of tasks) {
    if (picked.length >= want) break;
    if (picked.includes(tk)) continue;
    picked.push(tk);
  }
  return picked.slice(0, want).map((tk) => tk.steps);
}

export function formatQuestionsAsCustomizeTopic(
  steps: InterviewQuestion[],
  maxLen = 4000,
): string {
  const lines = steps.map((q, i) => `${i + 1}. ${(q.question_text || '').trim()}`);
  let out = lines.join('\n\n');
  if (out.length > maxLen) out = out.slice(0, maxLen);
  return out;
}

export function countPoolTasks(questions: InterviewQuestion[]): number {
  return groupQuestionsByTask(questions).size;
}
